import React, { useState, useEffect, useRef } from 'react';
import { FilePenLine, FileText, ChevronLeft, ChevronRight, MoreHorizontal, Trash2, Pencil } from 'lucide-react';
import { API_ROOT } from '@/config';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import SidebarProfile from './sidebar-profile';
import LoadingSpinner from './loading-spinner';

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

interface Submission {
  analysis_id: string;
  creator_email: string;
  creator_name: string;
  creator_first_name?: string;
  creator_surname?: string;
  video_fingerprint?: string;
  submission_status: string;
  compliance_score?: number;
  passing?: boolean;
  submitted_at: string;
  video_filename?: string;
}

interface Campaign {
  campaign_uuid: string;
  campaign_name: string;
  brand_name: string;
  product_name: string;
  created_at: string;
  submissions?: Submission[];
}

interface BrandCampaignsSidebarProps {
  collapsed?: boolean;
  onToggleSidebar?: () => void;
  onCampaignSelect?: (campaignUuid: string) => void;
  selectedCampaignUuid?: string | null;
  onNewCampaignClick?: () => void;
  refreshTrigger?: number;
  onStartTour?: () => void;
}

const BrandCampaignsSidebar: React.FC<BrandCampaignsSidebarProps> = ({ 
  collapsed = false,
  onToggleSidebar,
  onCampaignSelect,
  selectedCampaignUuid,
  onNewCampaignClick,
  refreshTrigger,
  onStartTour
}) => {
  const isCollapsed = collapsed;
  const [transitionEnabled, setTransitionEnabled] = useState(false);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedCampaign, setExpandedCampaign] = useState<string | null>(null);
  const [menuCampaign, setMenuCampaign] = useState<Campaign | null>(null);
  const [confirmCampaignUuid, setConfirmCampaignUuid] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameCampaignUuid, setRenameCampaignUuid] = useState<string | null>(null);
  const [newCampaignName, setNewCampaignName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setTransitionEnabled(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  useEffect(() => {
    if (refreshTrigger !== undefined) {
      fetchCampaigns();
    }
  }, [refreshTrigger]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuCampaign(null);
        // Don't clear confirmCampaignUuid here - it's needed for the delete modal
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchCampaigns = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        return;
      }

      const response = await fetch(`${API_ROOT}/brand/campaigns`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch campaigns');
      }

      const data = await response.json();
      setCampaigns(data.campaigns || []);
    } catch (err) {
      console.error('Error fetching campaigns:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      // Parse UTC timestamp from backend
      let date: Date;
      if (typeof dateString === 'string' && !dateString.includes('Z') && !dateString.includes('+')) {
        // SQLite returns "YYYY-MM-DD HH:MM:SS" in UTC
        const utcTimestamp = dateString.replace(' ', 'T') + 'Z';
        date = new Date(utcTimestamp);
      } else {
        date = new Date(dateString);
      }
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  const handleCampaignClick = (campaignUuid: string) => {
    if (expandedCampaign === campaignUuid) {
      setExpandedCampaign(null);
      if (onCampaignSelect) {
        onCampaignSelect('');
      }
    } else {
      setExpandedCampaign(campaignUuid);
      if (onCampaignSelect) {
        onCampaignSelect(campaignUuid);
      }
    }
  };

  const handleNewCampaign = () => {
    if (onNewCampaignClick) {
      onNewCampaignClick();
    }
  };

  const handleMenuToggle = (event: React.MouseEvent, campaign: Campaign) => {
    event.stopPropagation();
    // Don't clear confirmCampaignUuid here - it's needed for the delete modal
    setMenuCampaign((prev) =>
      prev?.campaign_uuid === campaign.campaign_uuid ? null : campaign
    );
  };

  const handleConfirmDelete = async () => {
    console.log('🗑️ handleConfirmDelete called, confirmCampaignUuid:', confirmCampaignUuid);
    if (!confirmCampaignUuid) {
      console.error('❌ No confirmCampaignUuid set');
      return;
    }
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('❌ No token found');
        setIsDeleting(false);
        return;
      }

      console.log('🗑️ Sending DELETE request to:', `${API_ROOT}/brand/campaigns/${confirmCampaignUuid}`);
      const response = await fetch(`${API_ROOT}/brand/campaigns/${confirmCampaignUuid}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      console.log('🗑️ Response status:', response.status);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Delete failed:', errorText);
        throw new Error('Failed to delete campaign');
      }

      console.log('✅ Campaign deleted successfully');
      setCampaigns((prev) => prev.filter((c) => c.campaign_uuid !== confirmCampaignUuid));
      if (selectedCampaignUuid === confirmCampaignUuid) {
        setExpandedCampaign(null);
        if (onCampaignSelect) {
          onCampaignSelect('');
        }
      }
      setMenuCampaign(null);
      setConfirmCampaignUuid(null);
      setShowDeleteModal(false);
    } catch (err) {
      console.error('❌ Error deleting campaign:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConfirmRename = async () => {
    if (!renameCampaignUuid || !newCampaignName.trim()) {
      return;
    }
    setIsRenaming(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('❌ No token found');
        setIsRenaming(false);
        return;
      }

      const response = await fetch(`${API_ROOT}/brand/campaigns/${renameCampaignUuid}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ campaign_name: newCampaignName.trim() }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Rename failed:', errorText);
        throw new Error('Failed to rename campaign');
      }

      console.log('✅ Campaign renamed successfully');
      // Update the campaign name in the local state
      setCampaigns((prev) =>
        prev.map((c) =>
          c.campaign_uuid === renameCampaignUuid
            ? { ...c, campaign_name: newCampaignName.trim() }
            : c
        )
      );
      setShowRenameModal(false);
      setRenameCampaignUuid(null);
      setNewCampaignName('');
      setMenuCampaign(null);
    } catch (err) {
      console.error('❌ Error renaming campaign:', err);
    } finally {
      setIsRenaming(false);
    }
  };

  return (
    <aside 
      style={{ 
        background: PALETTE.brightSnow,
        color: PALETTE.gunmetal,
        borderRight: `1px solid ${PALETTE.americanSilver}`,
        width: isCollapsed ? '72px' : '256px'
      }} 
      className={`brand-sidebar h-screen flex flex-col overflow-visible ${transitionEnabled ? 'transition-[width] duration-300 ease-in-out' : ''}`}
    >
      {/* Fixed Top Section */}
      <div className="flex-shrink-0">
        {/* Header with logo and hide button */}
        <div className="flex items-center justify-between px-4 py-3">
          <div 
            className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={handleNewCampaign}
            title="Start New Campaign"
          >
            <img 
              src="/brandcomply_logo.png" 
              alt="brandcomply logo" 
              className="h-6 w-6 flex-shrink-0 object-contain"
            />
          </div>

          {onToggleSidebar && (
            <button
              onClick={onToggleSidebar}
              style={{
                background: 'transparent', 
                border: 'none', 
                color: PALETTE.gunmetal, 
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px'
              }}
              className="hover:bg-gray-100 transition-all hide-sidebar-button"
              title={isCollapsed ? "Show sidebar" : "Hide sidebar"}
            >
              {isCollapsed ? (
                <ChevronRight className="w-5 h-5" />
              ) : (
                <ChevronLeft className="w-5 h-5" />
              )}
            </button>
          )}
        </div>

        {/* New Campaign Button */}
        <div
          onClick={handleNewCampaign}
          className="new-campaign-btn flex items-center gap-3 mx-3 my-3 px-3 py-2 rounded-lg cursor-pointer transition-all hover:opacity-80 justify-start"
          style={{ 
            background: PALETTE.inchworm,
            color: PALETTE.gunmetal
          }}
          title="New Campaign"
        >
          <FilePenLine className="w-4 h-4 flex-shrink-0" />
          {!isCollapsed && (
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium whitespace-nowrap">
                New Campaign
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Scrollable Campaigns Section */}
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        {!isCollapsed && (
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 py-3">
            <div className="space-y-1">
            {/* Loading state */}
            {isLoading && (
              <div className="py-8">
                <LoadingSpinner size="md" text="Loading campaigns..." />
              </div>
            )}

            {/* Campaigns List */}
            {!isLoading && campaigns.length > 0 && (
              <div className="brand-campaigns-list space-y-1">
                {!isCollapsed && (
                  <div className="px-2 py-1">
                    <div className="text-xs font-semibold opacity-60" style={{ color: PALETTE.gunmetal }}>
                      Campaigns
                    </div>
                  </div>
                )}
                {campaigns.map((campaign) => {
                  const isMenuOpen = menuCampaign?.campaign_uuid === campaign.campaign_uuid;
                  return (
                    <div
                      key={campaign.campaign_uuid}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-all hover:bg-gray-100 group ${selectedCampaignUuid === campaign.campaign_uuid ? 'bg-gray-100' : ''}`}
                      onClick={() => handleCampaignClick(campaign.campaign_uuid)}
                      title={`${campaign.campaign_name} • ${formatDate(campaign.created_at)}`}
                    >
                      <FileText className="w-4 h-4 flex-shrink-0" style={{ color: PALETTE.gunmetal }} />
                      {!isCollapsed && (
                        <div className="flex-1 min-w-0">
                          <div className="text-sm truncate" style={{ color: PALETTE.gunmetal }}>
                            {campaign.campaign_name}
                          </div>
                          <div className="text-xs opacity-60" style={{ color: PALETTE.gunmetal }}>
                            {formatDate(campaign.created_at)}
                          </div>
                        </div>
                      )}
                      {!isCollapsed && (
                        <div className="relative ml-auto" ref={isMenuOpen ? menuRef : null}>
                          <button
                            className="p-1 rounded hover:bg-gray-200 transition-colors"
                            onClick={(event) => handleMenuToggle(event, campaign)}
                            onMouseDown={(event) => event.stopPropagation()}
                            title="Campaign options"
                          >
                            <MoreHorizontal className="w-4 h-4" style={{ color: PALETTE.gunmetal }} />
                          </button>
                          {isMenuOpen && (
                            <div
                              className="absolute right-0 top-7 w-44 rounded-xl border shadow-lg bg-white py-1 z-40"
                              style={{ borderColor: PALETTE.americanSilver }}
                              onMouseDown={(event) => event.stopPropagation()}
                            >
                              {/* Rename option */}
                              <button
                                type="button"
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors"
                                style={{ color: PALETTE.gunmetal }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRenameCampaignUuid(campaign.campaign_uuid);
                                  setNewCampaignName(campaign.campaign_name);
                                  setShowRenameModal(true);
                                  setMenuCampaign(null); // Close the dropdown menu
                                }}
                                disabled={isRenaming}
                              >
                                <span className="flex items-center gap-2">
                                  <Pencil className="w-4 h-4" />
                                  Rename
                                </span>
                              </button>
                              {/* Delete option */}
                              <button
                                type="button"
                                className="w-full text-left px-3 py-2 text-sm hover:bg-red-50 transition-colors"
                                style={{ color: '#ef4444' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  console.log('🗑️ Dropdown delete clicked, campaign:', campaign.campaign_uuid);
                                  setConfirmCampaignUuid(campaign.campaign_uuid);
                                  setShowDeleteModal(true);
                                  setMenuCampaign(null); // Close the dropdown menu
                                }}
                                disabled={isDeleting}
                              >
                                <span className="flex items-center gap-2">
                                  <Trash2 className="w-4 h-4" />
                                  Delete
                                </span>
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Empty state */}
            {!isLoading && campaigns.length === 0 && (
              <div className="text-center py-8" style={{ color: PALETTE.gunmetal }}>
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No campaigns yet</p>
                <p className="text-xs opacity-60 mt-1">Create a campaign to get started</p>
              </div>
            )}
            </div>
          </div>
        )}
      </div>

      {/* Fixed Bottom Section - User Profile */}
      <div className="sidebar-profile-btn flex-shrink-0 border-t overflow-visible relative z-10" style={{ borderColor: PALETTE.americanSilver }}>
        <SidebarProfile collapsed={isCollapsed} onStartTour={onStartTour} />
      </div>

      {showDeleteModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
          onClick={(e) => {
            e.stopPropagation();
            setShowDeleteModal(false);
            setConfirmCampaignUuid(null);
          }}
        >
          <div 
            className="w-[440px] rounded-xl bg-white shadow-lg border px-6 py-5"
            style={{ borderColor: PALETTE.americanSilver }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-base font-semibold mb-2" style={{ color: PALETTE.gunmetal }}>
              Delete this campaign permanently?
            </div>
            <div className="text-sm mb-5" style={{ color: `${PALETTE.gunmetal}80` }}>
              This will remove the campaign and its submissions.
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                className="text-sm px-5 py-2 rounded-lg border hover:bg-gray-50 transition-colors"
                style={{ borderColor: PALETTE.americanSilver, color: PALETTE.gunmetal }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteModal(false);
                  setConfirmCampaignUuid(null);
                  setMenuCampaign(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="text-sm px-5 py-2 rounded-lg hover:bg-red-600 transition-colors"
                style={{ background: '#ef4444', color: PALETTE.brightSnow }}
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('🗑️ Delete button clicked');
                  handleConfirmDelete();
                }}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Campaign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Modal */}
      {showRenameModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
          onClick={(e) => {
            e.stopPropagation();
            setShowRenameModal(false);
            setRenameCampaignUuid(null);
            setNewCampaignName('');
          }}
        >
          <div 
            className="w-[440px] rounded-xl bg-white shadow-lg border px-6 py-5"
            style={{ borderColor: PALETTE.americanSilver }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-base font-semibold mb-4" style={{ color: PALETTE.gunmetal }}>
              Rename Campaign
            </div>
            <input
              type="text"
              value={newCampaignName}
              onChange={(e) => setNewCampaignName(e.target.value)}
              placeholder="Enter campaign name"
              className="w-full px-3 py-2 rounded-lg border text-sm mb-5 focus:outline-none focus:ring-2"
              style={{ 
                borderColor: PALETTE.americanSilver, 
                color: PALETTE.gunmetal,
                '--tw-ring-color': PALETTE.inchworm 
              } as React.CSSProperties}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newCampaignName.trim()) {
                  handleConfirmRename();
                }
              }}
            />
            <div className="flex justify-end gap-3">
              <button
                type="button"
                className="text-sm px-5 py-2 rounded-lg border hover:bg-gray-50 transition-colors"
                style={{ borderColor: PALETTE.americanSilver, color: PALETTE.gunmetal }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowRenameModal(false);
                  setRenameCampaignUuid(null);
                  setNewCampaignName('');
                  setMenuCampaign(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="text-sm px-5 py-2 rounded-lg hover:opacity-90 transition-colors"
                style={{ background: PALETTE.inchworm, color: PALETTE.gunmetal }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleConfirmRename();
                }}
                disabled={isRenaming || !newCampaignName.trim()}
              >
                {isRenaming ? 'Renaming...' : 'Rename'}
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

export default BrandCampaignsSidebar;

